import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 7.2 visual preview at /preview/lesson/7.2
 * "The teammate who over-trusts AI"
 * The human override: how to catch the work without embarrassing the person.
 */

export default function LessonPreviewPage7_2() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

const SECTION_IDS = [
  'sec-cold-open',
  'sec-from-last-time',
  'sec-teaching',
  'sec-your-turn',
  'sec-quick-check',
  'sec-lock-card',
];

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = SECTION_IDS.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromPrev />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 7 · Working with people who use AI differently · Lesson 7.2
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        The teammate who over-trusts AI
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 40 min
        </span>
        <span className="text-line">|</span>
        <span>Threads: Override · Posture · Repair</span>
      </div>
      <p className="mt-3 text-xs font-mono uppercase tracking-wider text-amber">
        3 human-override drills
      </p>
    </header>
  );
}

/* --- Section 1: Cold open --- */
function ColdOpen() {
  return (
    <Section id="sec-cold-open" kicker="Cold open" title="The deck looks great. The numbers do not match the company's actual filings. The presentation is in 90 minutes.">
      <p className="mt-6 text-text/90">
        A teammate on a college case competition sends the final deck to the
        group chat. Twenty slides. Crisp. He is proud of it, and the team is
        relieved because the deadline is tight.
      </p>
      <p className="mt-4 text-text/90">
        You open slide 7. It says the target company&rsquo;s revenue is
        $4.2 billion. You happen to know it is $3.1 billion, because you read
        the latest earnings release two days ago. You check slide 12: a
        founder quote that is too perfectly phrased. You search it. No
        results. The quote does not exist.
      </p>
      <p className="mt-4 text-text/90">
        He used AI to draft the deck. He did not check the AI. The
        presentation is in 90 minutes. You have two problems now, not one.
      </p>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/[0.06] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
            Problem 01 · The work
          </p>
          <p className="mt-3 text-text/90">
            The deck has at least two factual errors a judge will catch in
            ten seconds. The team will lose. You cannot ship this as it is.
          </p>
        </div>
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/[0.06] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
            Problem 02 · The person
          </p>
          <p className="mt-3 text-text/90">
            Your teammate is sitting next to you, proud of the deck, with
            90 minutes on the clock. How you bring this up decides whether
            the team comes apart before it even presents.
          </p>
        </div>
      </div>

      <p className="mt-6 text-text/90">
        Lesson 7.2 is about the move that handles both at once. Catch the
        work. Protect the person.
      </p>
    </Section>
  );
}

/* --- Section 2: From 7.1 --- */
function FromPrev() {
  return (
    <Section id="sec-from-last-time" kicker="From last time · Lesson 7.1" title="Reading the tell is step one. Doing something about it without breaking the team is step two.">
      <p className="mt-6 text-text/90">
        Lesson 7.1 trained you to notice the small signals: vocabulary shift,
        speed mismatch, “as we discussed,” defense reaction,
        refusal. Today is what happens after you notice. Specifically, what
        you do when a teammate has shipped AI output without checking it,
        and your name is also on the work.
      </p>
      <p className="mt-4 text-text/90">
        The instinct is one of two extremes: stay quiet and let it ship, or
        call it out in the group chat and burn the room. Both are bad.
        Lesson 7.2 is the third move.
      </p>
      <ol className="mt-6 space-y-4">
        <ReviewItem n="01" q="Two of the five tells from 7.1, in your own words." />
        <ReviewItem n="02" q="The 7.1 posture, in one phrase: work, not ___." />
        <ReviewItem n="03" q="Why the two extremes (stay quiet, or call out in public) both fail the team." />
      </ol>
    </Section>
  );
}

function ReviewItem({ n, q }: { n: string; q: string }) {
  return (
    <li className="flex gap-4">
      <span className="font-mono text-sm text-amber pt-1">{n}.</span>
      <span className="text-text/90">{q}</span>
    </li>
  );
}

/* --- Section 3: Teaching --- */
function Teaching() {
  return (
    <Section id="sec-teaching" kicker="Teaching · The human override" title="There is a way to stop bad output without humiliating anyone. That move is what this lesson teaches.">
      <Subhead n={1} title="The vocab for the lesson" />
      <VocabCard
        term="Human override"
        definition="A move that stops AI output from shipping when it should not, without humiliating the teammate who put it there. A human override is private when possible, framed about the work, surgical (one issue at a time), and offers a path forward, not just a problem. On a team, this is the cousin of human-in-the-loop: not just deciding alongside AI, but deciding to pull the plug when AI's draft is wrong."
        analogy="A good editor in a newsroom catches a bad lede before it prints. They do not announce it on the floor. They walk over, point at one sentence, and ask &ldquo;walk me through this?&rdquo; The reporter fixes it. The paper goes out clean. That is a human override done right: the system stops, the person is intact."
        memoryHook="A human override protects two things at once: the work, and the teammate. If you only protect one, you are not overriding. You are either letting it ship or calling someone out."
        why="On a team you will face this constantly. The cost of not overriding is shipping bad work with your name on it. The cost of overriding badly is losing the teammate you need next month. The human override is the only move that does not require you to choose between the work and the relationship. It is also the move the AI-safety field is built around: knowing when, and how, to say stop."
      />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          Also worth knowing
        </p>
        <h4 className="mt-1 text-base font-semibold text-text">Prompt injection</h4>
        <p className="mt-3 text-text/90">
          A failure mode where text inside a document, a webpage, or a pasted block of
          source material tells the AI to do something different from what the user
          asked. The AI cannot tell the difference between &ldquo;these are instructions&rdquo;
          and &ldquo;this is content to read.&rdquo; If a teammate hands you a draft built from
          AI summarizing a sketchy PDF or website, and the output suddenly reads weird,
          off-topic, or wrong, prompt injection is one of the things to suspect.
        </p>
        <p className="mt-3 text-sm text-muted">
          The override move here: do not trust AI summaries of untrusted documents
          without spot-checking them against the source. The fix is the same audit
          habit from Week 3, applied at the team layer.
        </p>
      </div>

      <Subhead n={2} title="The four parts of a real override" className="mt-12" />
      <p className="mt-4 text-text/90">
        Most attempts to catch a teammate&apos;s AI mistake fail because they
        skip one of these. Each one matters. Each one is a choice.
      </p>
      <PartsGrid />

      <Subhead n={3} title="The three-channel rule" className="mt-12" />
      <p className="mt-4 text-text/90">
        Where you raise the override matters as much as how. Same words, wrong
        channel, the team breaks. Same words, right channel, the work goes
        out clean.
      </p>
      <div className="mt-6 rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
          Where to override
        </p>
        <ol className="mt-3 space-y-3 text-text/90">
          <li><span className="font-mono text-xs text-amber mr-2">01.</span> 1:1 DM or pull-aside: default for anything that questions someone&apos;s ownership of work. Always start here if there is time.</li>
          <li><span className="font-mono text-xs text-amber mr-2">02.</span> Inline doc comment: fine for one factual issue with a source attached. Not for &ldquo;did you actually write this?&rdquo;</li>
          <li><span className="font-mono text-xs text-amber mr-2">03.</span> Group chat or meeting: only when the deadline is now and there is no other option. Even then, frame about the work, never the person.</li>
        </ol>
      </div>

      <Subhead n={4} title="Where AI helps and where AI hurts on overriding" className="mt-12" />
      <p className="mt-4 text-text/90">
        AI is useful for preparing the override. It is bad at delivering one,
        because an override is a human read of how the other person
        will hear it.
      </p>
      <SplitGrid />

      <Subhead n={5} title="The repair step" className="mt-12" />
      <p className="mt-4 text-text/90">
        An override is not finished when the error is fixed. It is finished when
        the teammate knows you are still on their side. Skip the repair and
        you have not caught the work, you have built a story they will tell
        about you for a year.
      </p>
      <div className="mt-6 rounded-lg border border-emerald-500/40 bg-emerald-500/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
          Two lines that finish the override
        </p>
        <p className="mt-3 text-text/90">
          &ldquo;Glad we caught it together before the meeting.&rdquo; (puts
          the work first, makes it a team save, not a rescue.)
        </p>
        <p className="mt-3 text-text/90">
          &ldquo;Honestly, AI does this to me all the time too.&rdquo; (drops
          the hierarchy. You are not above the mistake. You just happened to
          spot it this time.)
        </p>
      </div>
    </Section>
  );
}

const PARTS = [
  {
    n: '01',
    name: 'Private when possible',
    body: 'Default to DM, hallway, or pull-aside. Public catches feel like callouts even when they are not. The exception: a hard deadline with no time to pull aside, and even then you frame the issue, not the person.',
  },
  {
    n: '02',
    name: 'About the work, never the person',
    body: '“This number does not match the filing” is about the work. “Did you actually check this?” is about the person. Same content, completely different conversation. The first one fixes the deck. The second one starts a fight.',
  },
  {
    n: '03',
    name: 'Surgical: one issue at a time',
    body: 'You probably found three problems. Start with the one that breaks the work hardest. Save the rest for after the fire is out. A teammate can hear one issue and fix it. They cannot hear five at once and stay in the room with you.',
  },
  {
    n: '04',
    name: 'Offer a path forward',
    body: 'Do not just point at the broken thing. Have the next move ready: “I pulled the right number from the 10-K, want me to drop it in?” The catch is not “you broke this.” The catch is “we have 90 minutes and here is how we fix it.”',
  },
];

function PartsGrid() {
  return (
    <div className="mt-6 space-y-4">
      {PARTS.map((p) => (
        <div key={p.n} className="rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="font-mono text-xs text-muted">Part {p.n}</span>
            <h4 className="text-lg font-semibold text-amber">{p.name}</h4>
          </div>
          <p
            className="mt-3 text-text/90"
            dangerouslySetInnerHTML={{ __html: p.body }}
          />
        </div>
      ))}
    </div>
  );
}

const SPLIT = [
  {
    side: 'AI helps',
    items: [
      'Verifying the actual number, quote, source, or claim before you raise the override.',
      'Drafting two or three versions of the message so you can pick the one that lands softest.',
      'Pulling the correct fact from a primary source so you can offer it as the fix.',
      'Rehearsing the conversation if it is a teammate you are nervous to approach.',
    ],
    accent: 'emerald' as const,
  },
  {
    side: 'AI hurts',
    items: [
      'Telling you how the teammate will actually take it. AI cannot read your specific person.',
      'Picking the channel: AI does not know the team dynamic or the deadline pressure.',
      'Writing the repair line for you. Repair only works in your own voice, not a generated one.',
      'Auditing your motive. If you are overriding to look right, AI will help you do it. Notice.',
    ],
    accent: 'rose' as const,
  },
];

function SplitGrid() {
  return (
    <div className="mt-6 grid gap-4 lg:grid-cols-2">
      {SPLIT.map((col) => {
        const isEm = col.accent === 'emerald';
        const borderCls = isEm ? 'border-emerald-500/40' : 'border-rose-500/40';
        const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-rose-500/[0.04]';
        const labelCls = isEm ? 'text-emerald-300' : 'text-rose-300';
        return (
          <div key={col.side} className={`rounded-lg border ${borderCls} ${bgCls} p-5`}>
            <p className={`font-mono text-[11px] uppercase tracking-wider ${labelCls}`}>
              {col.side}
            </p>
            <ul className="mt-3 space-y-2">
              {col.items.map((it, i) => (
                <li key={i} className="flex gap-2 text-sm text-text/90">
                  <span className={`shrink-0 ${labelCls}`}>·</span>
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

/* --- Section 4: Your turn --- */
function YourTurn() {
  return (
    <Section id="sec-your-turn" kicker="Your turn · Pick the right override" title="Three scenes. Two possible moves. Pick the one that overrides the work without breaking the team.">
      <p className="mt-6 text-text/90">
        Each drill shows a real situation. Both options will technically fix
        the error. Only one keeps the teammate on your side for next time.
        Pick the move that is the actual override.
      </p>
      <div className="mt-8 space-y-6">
        {DRILLS.map((d) => (
          <CatchDrill key={d.n} drill={d} />
        ))}
      </div>
    </Section>
  );
}

type DrillSpec = {
  n: string;
  scene: string;
  optionA: string;
  optionB: string;
  correct: 'A' | 'B';
  verdict: string;
};

const DRILLS: DrillSpec[] = [
  {
    n: '01',
    scene:
      'College case competition deck, 90 minutes to present. A teammate sent the final deck. You spot a clearly wrong revenue number on slide 7 and a quote on slide 12 that you cannot find anywhere online. He is two seats away, headphones in, looking proud.',
    optionA:
      'Reply-all in the group chat: “Hey, slide 7 revenue is wrong (actual is $3.1B per the latest earnings) and the slide 12 quote does not exist on Google. Did you check these? We need to fix before presenting.”',
    optionB:
      'Tap him on the shoulder, pull up the 10-K on your laptop: “Hey, want to sanity-check two slides with me real quick? I might be wrong but I think slide 7 has $4.2B and the filing shows $3.1B. Want me to drop the right number in?”',
    correct: 'B',
    verdict:
      'Option B does everything an override is supposed to do: private (just the two of you), about the work (the number, not him), surgical (one issue first, the quote can come 30 seconds later), and offers the path forward (you already have the right number ready). Option A is technically correct and might also fix the deck, but it puts him on the defensive in front of the whole team with 90 minutes on the clock. Even if you win the round, you have lost the teammate you need for the rebuttal.',
  },
  {
    n: '02',
    scene:
      'You are on a robotics team. A teammate posts a long technical writeup in the team channel explaining how a sensor works. It is well-written but contains a description of a calibration method that does not match the sensor\'s documentation. The writeup is going to a sponsor on Friday. It is Tuesday.',
    optionA:
      'Drop an inline doc comment on the specific paragraph: “Quick check on this calibration step. The Bosch BMI270 datasheet (page 14) describes the procedure differently. Want to compare notes before this goes to the sponsor?”',
    optionB:
      'Wait until the writeup is sent on Friday, then mention it in the post-mortem the next week as a lesson learned for the team.',
    correct: 'A',
    verdict:
      'You have three days. That is plenty of time for the lowest-stakes channel that still works: an inline doc comment, attached to a primary source. The teammate can fix it quietly. Nobody is embarrassed. The sponsor gets accurate work. Option B is the worst possible move: you knew about an error, watched it go to a sponsor, and saved your override for after the damage was done. That is not overriding, it is collecting evidence on a teammate.',
  },
  {
    n: '03',
    scene:
      'First month at a new job. In your manager\'s 1:1 with you, she mentions she is presenting a quarterly metrics review to the leadership team tomorrow. She mentions one number that you happen to know is calculated wrong (you saw the source spreadsheet last week). You are brand new. She is your manager.',
    optionA:
      'Stay quiet. You are new, the number might be right and you might be confused, and it is not your place to correct your manager on something this senior.',
    optionB:
      'Right after the 1:1, send a short Slack message: “Quick one before tomorrow. The retention number you mentioned looks different in the source sheet I saw last week (X vs Y). Could be a definition difference or a stale view on my end. Want me to send you the link so you can double-check before the leadership review?”',
    correct: 'B',
    verdict:
      'Brand new or not, the override is part of the job. Option B does it well: private channel, framed as a question (could be a definition difference), offers the source, gives her time to verify before the meeting. She presents a correct number and remembers that the new hire pays attention. Option A protects your comfort and exposes your manager. Hierarchies do not exempt anyone from an override; they just change the tone and the channel.',
  },
];

function CatchDrill({ drill }: { drill: DrillSpec }) {
  const [picked, setPicked] = useState<'A' | 'B' | null>(null);
  const wasWrong = picked !== null && picked !== drill.correct;

  return (
    <div className="rounded-lg border border-line bg-surface p-5">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Drill {drill.n} · The scene
      </p>
      <p
        className="mt-3 text-text/90"
        dangerouslySetInnerHTML={{ __html: drill.scene }}
      />

      <div className="mt-5 border-t border-line pt-4">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Which move is the real override?
        </p>
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          {(['A', 'B'] as const).map((key) => {
            const text = key === 'A' ? drill.optionA : drill.optionB;
            const isPicked = picked === key;
            const isCorrect = key === drill.correct;
            const shouldShowCorrect = picked !== null && isCorrect;
            const shouldShowWrong = isPicked && !isCorrect;
            let cls =
              'rounded-md border border-line bg-elevated p-4 text-left text-sm text-text/85 hover:border-amber transition-colors';
            if (shouldShowCorrect)
              cls =
                'rounded-md border border-emerald-500 bg-emerald-500/[0.08] p-4 text-left text-sm text-text';
            if (shouldShowWrong)
              cls =
                'rounded-md border border-rose-500 bg-rose-500/[0.08] p-4 text-left text-sm text-text';
            return (
              <button
                key={key}
                className={cls}
                onClick={() => setPicked(key)}
              >
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted block">
                  Option {key}
                </span>
                <span
                  className="mt-2 block"
                  dangerouslySetInnerHTML={{ __html: text }}
                />
              </button>
            );
          })}
        </div>

        {wasWrong && (
          <div className="mt-4 rounded-md border border-rose-500/40 bg-rose-500/[0.06] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
              Not this one · Option {picked}
            </p>
            <p className="mt-2 text-sm text-text/85">
              That move might fix the error, but it costs the teammate or
              ships the damage. A real override protects both.
            </p>
          </div>
        )}

        {picked !== null && (
          <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
              {wasWrong ? 'The real override' : 'Correct'} · Option {drill.correct}
            </p>
            <p className="mt-2 text-sm text-text/90">{drill.verdict}</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* --- Section 5: Quick check --- */
function QuickCheck() {
  return (
    <Section id="sec-quick-check" kicker="Quick check" title="Five questions. Answer first. Then reveal.">
      <div className="mt-6 space-y-4">
        <QuickCheckItem n="Q1" q="Define Human override in one sentence." answer="A move that stops AI output from shipping when it should not, without humiliating the teammate who wrote it: private when possible, about the work, surgical, with a path forward." />
        <QuickCheckItem n="Q2" q="The four parts of a real human override." answer="Private when possible. About the work, never the person. Surgical (one issue at a time). Offers a path forward, not just a problem." />
        <QuickCheckItem n="Q3" q="The three-channel rule, in order." answer="1:1 DM or pull-aside (default). Inline doc comment (for a single factual issue with a source). Group chat or meeting (only when deadline forces it, and only framed about the work)." />
        <QuickCheckItem n="Q4" q="Two things AI helps with on a human override. Two things it hurts." answer="Helps: verifying the actual fact, drafting versions of the message, pulling the right source, rehearsing the conversation. Hurts: predicting how the person will take it, picking the channel, writing the repair line, auditing whether you are overriding to fix or to look right." />
        <QuickCheckItem n="Q5" q="Why the repair step matters." answer="An override is not finished when the error is fixed. It is finished when the teammate knows you are still on their side. Without repair, you have a story they tell about you for a year." />
      </div>
    </Section>
  );
}

/* --- Section 6: Lock card --- */
function LockCard() {
  return (
    <Section id="sec-lock-card" kicker="Lock card" title="Screenshot this. The human override map.">
      <div className="mt-6 rounded-2xl border border-amber/40 bg-gradient-to-b from-amber/[0.06] to-transparent p-7" data-lockcard-body>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
              Lesson 7.2 · Lock card · The teammate who over-trusts AI
            </p>
          </div>
          <button className="font-mono text-[10px] uppercase tracking-wider text-muted hover:text-text">
            ↓ Save image
          </button>
        </div>
        <h3 className="mt-4 text-2xl font-semibold leading-snug text-text">
          Catch the work. Protect the person. If you can only do one, you are
          not overriding, you are letting it ship or calling someone out.
        </h3>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <LockTile n="01" name="Private" body="DM or pull-aside by default. Public overrides feel like callouts even when they are not." />
          <LockTile n="02" name="About the work" body='"This number does not match the filing." Not "did you actually check this?"' />
          <LockTile n="03" name="Surgical" body="One issue at a time. Start with the one that breaks the work hardest." />
          <LockTile n="04" name="Path forward" body='Have the fix ready. "I pulled the right number, want me to drop it in?"' />
        </div>

        <div className="mt-6 rounded-md border border-line bg-surface p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Where to override (channel order)
          </p>
          <p className="mt-2 text-text/90">
            1:1 DM or pull-aside (default). Inline doc comment (one factual
            issue, with a source). Group chat or meeting (only when the
            deadline forces it, and only about the work).
          </p>
        </div>

        <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
            The repair line
          </p>
          <p className="mt-2 text-text/90">
            &ldquo;Glad we caught it together before the meeting.&rdquo;
            Plus: &ldquo;Honestly, AI does this to me all the time too.&rdquo;
            That finishes the override.
          </p>
        </div>

        <p className="mt-6 text-sm text-muted italic">
          A human override protects two things. If it only protects one, it is
          not an override.
        </p>
      </div>
    </Section>
  );
}

function LockTile({ n, name, body }: { n: string; name: string; body: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[10px] text-muted">{n}</span>
        <span className="rounded bg-amber/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber">
          {name}
        </span>
      </div>
      <p
        className="mt-3 text-sm text-text/85"
        dangerouslySetInnerHTML={{ __html: body }}
      />
    </div>
  );
}

/* --- Next up --- */
function NextUp() {
  return (
    <div className="mt-14 rounded-xl border border-line bg-surface p-6" data-nextup-card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Up next · Lesson 7.3
          </p>
          <h3 className="mt-2 text-xl font-semibold text-text">
            The teammate who refuses to use AI
          </h3>
          <p className="mt-3 text-sm text-text/85">
            The opposite problem. Vocab: Bridge. How to work with a teammate
            who will not touch AI, without hiding the fact that you do, and
            without making them feel like the team is leaving them behind.
          </p>
        </div>
        <button className="rounded-md border border-amber/40 px-4 py-2 text-sm font-medium text-amber hover:bg-amber/[0.08]">
          Coming soon
        </button>
      </div>
    </div>
  );
}

/* --- Primitives --- */

function Section({
  id,
  kicker,
  title,
  children,
}: {
  id: string;
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-16 scroll-mt-24">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        {kicker}
      </p>
      <h2 className="mt-3 text-2xl font-semibold leading-snug text-text">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function Subhead({ n, title, className = '' }: { n: number; title: string; className?: string }) {
  return (
    <h3 className={`text-xl font-semibold text-text ${className}`}>
      <span className="mr-3 font-mono text-sm text-amber">{n}</span>
      {title}
    </h3>
  );
}

function VocabCard({
  term,
  definition,
  analogy,
  memoryHook,
  why,
}: {
  term: string;
  definition: string;
  analogy: string;
  memoryHook: string;
  why: string;
}) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="mt-6 rounded-xl border border-amber/40 bg-amber/[0.04] p-6">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Vocab · 7.2 · New term
      </p>
      <h4 className="mt-2 text-2xl font-semibold text-text">{term}</h4>
      {!revealed ? (
        <div className="mt-5">
          <p className="text-sm text-text/85">
            Write your own one-sentence definition first. Then reveal.
          </p>
          <textarea
            className="mt-3 w-full rounded-md border border-line bg-elevated p-3 text-sm text-text placeholder:text-muted focus:border-amber focus:outline-none"
            rows={3}
            placeholder={`Your definition of "${term}"...`}
          />
          <button
            className="mt-3 rounded-md bg-amber px-4 py-2 text-sm font-medium text-ink hover:opacity-90"
            onClick={() => setRevealed(true)}
          >
            Reveal definition →
          </button>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <VocabBlock label="Definition" body={definition} />
          <VocabBlock label="Analogy" body={analogy} accent />
          <VocabBlock label="Memory hook" body={memoryHook} />
          <VocabBlock label="Why it matters" body={why} />
        </div>
      )}
    </div>
  );
}

function VocabBlock({ label, body, accent = false }: { label: string; body: string; accent?: boolean }) {
  return (
    <div
      className={`rounded-md border ${accent ? 'border-amber/40 bg-amber/[0.04]' : 'border-line bg-surface'} p-4`}
    >
      <p className={`font-mono text-[10px] uppercase tracking-wider ${accent ? 'text-amber' : 'text-muted'}`}>
        {label}
      </p>
      <p className="mt-2 text-sm text-text/90">{body}</p>
    </div>
  );
}

function QuickCheckItem({ n, q, answer }: { n: string; q: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3">
          <span className="font-mono text-xs text-amber pt-0.5">{n}</span>
          <p className="text-text/90" dangerouslySetInnerHTML={{ __html: q }} />
        </div>
        <button
          onClick={() => setOpen(!open)}
          className="shrink-0 rounded-md border border-line px-3 py-1 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
      {open && (
        <p
          className="mt-3 border-t border-line pt-3 text-sm text-text/85"
          dangerouslySetInnerHTML={{ __html: answer }}
        />
      )}
    </div>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    'Cold open',
    'From 7.1',
    'The override',
    'Your turn',
    'Quick check',
    'Lock card',
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ol className="mt-4 space-y-1">
        {items.map((label, i) => {
          const isActive = i === activeIndex;
          const isPast = i < activeIndex;
          return (
            <li key={label}>
              <a
                href={`#${SECTION_IDS[i]}`}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-mono ${
                    isActive
                      ? 'bg-amber text-ink'
                      : isPast
                      ? 'border border-amber/60 text-amber'
                      : 'border border-line text-muted'
                  }`}
                >
                  {isPast ? '✓' : i + 1}
                </span>
                <span>{label}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
